<script setup>
/**
 * Every WhatsApp broadcast this workspace has sent. THE PLAN, §4 P10.
 *
 * WHY THIS FILE LIVES UNDER `campaigns/` AND NOT UNDER `wasloai/`. Broadcasts left the AI
 * section deliberately: it is outbound marketing that costs money per message, it shares no
 * engine with authoring an assistant, and it was the one tab in the seven-tab UI that had
 * nothing to do with the other six. It is a child of Chatwoot's own Campaigns group now,
 * so it sits beside the other things that go OUT — and the page still uses the Waslo frame,
 * the Waslo BFF client and the Waslo copy table, because those are the section's tools
 * rather than the section's address. `waslo-lint` covers this directory for exactly that
 * reason (THE PLAN, §5).
 *
 * IT MOUNTS INSIDE `CampaignsPageRouteView`, WHICH IS A `<keep-alive>`. That is a real
 * constraint and not a footnote: `onMounted` runs once, so coming back from a detail page
 * to a list that had gone stale would show yesterday's counts on a send that has since
 * finished. `onActivated` re-reads — but only after the first mount, or the page would fire
 * two identical requests on its way in.
 */
import { computed, onActivated, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import BroadcastRow from 'dashboard/routes/dashboard/wasloai/components/broadcast/BroadcastRow.vue';
import NotSetUp from 'dashboard/routes/dashboard/wasloai/components/broadcast/NotSetUp.vue';
import WasloExplainer from 'dashboard/routes/dashboard/wasloai/components/WasloExplainer.vue';
import WasloPageLayout from 'dashboard/routes/dashboard/wasloai/components/WasloPageLayout.vue';
import { useWasloI18n } from 'dashboard/routes/dashboard/wasloai/i18n.js';
import { useWasloApi } from 'dashboard/routes/dashboard/wasloai/composables/useWasloApi.js';
import { useWasloAccountId } from 'dashboard/routes/dashboard/wasloai/composables/useWasloAccountId.js';
import {
  WASLO_ERROR_CODES,
  isBroadcastNotSetUp,
  wasloApi,
} from 'dashboard/routes/dashboard/wasloai/api/client.js';

const { t } = useWasloI18n();
const router = useRouter();
const accountId = useWasloAccountId();

const broadcasts = useWasloApi(signal =>
  wasloApi.getBroadcasts(accountId.value, signal)
);

/**
 * READ ONLY TO LEARN WHETHER SENDING WAS EVER SET UP. The list cannot say: it is a plain
 * read of what was sent, and a workspace with no sending set up answers it with an empty
 * list, which on its own renders "Send your first broadcast" and a button that can only
 * fail. The channel catalogue is the call that needs the sending credentials, so it is the
 * one that answers the 409 `broadcast_not_provisioned`. Any other failure of it is not
 * this page's business: the list it would sit beside loaded fine.
 */
const channels = useWasloApi(
  signal => wasloApi.getBroadcastChannels(accountId.value, signal),
  { isEmpty: () => false }
);

const reload = () => {
  broadcasts.reload();
  channels.reload();
};

const hasMounted = ref(false);
onMounted(() => {
  hasMounted.value = true;
});
onActivated(() => {
  if (hasMounted.value) reload();
});

const rows = computed(() => broadcasts.data.value ?? []);

/**
 * A workspace whose WhatsApp number was never set up for sending gets the sidecar's 409,
 * and it is a REAL, NAMEABLE STATE rather than a failure: "we couldn't load this" would
 * send an owner to check her connection for something no amount of retrying will fix. So
 * it renders as an empty state with the one sentence that is actually true — contact us.
 */
const notSetUp = computed(
  () =>
    isBroadcastNotSetUp(broadcasts.error.value) ||
    isBroadcastNotSetUp(channels.error.value)
);

const errorMessage = computed(() => {
  const error = broadcasts.error.value;
  if (!error || notSetUp.value) return '';
  return error.code === WASLO_ERROR_CODES.NOT_PROVISIONED
    ? t('WASLO.BROADCASTS.NOT_SET_UP.BODY')
    : t('WASLO.COMMON.ERROR_BODY');
});

// The 409 is an `error` as far as the composable is concerned, so `isEmptyState` is false
// for it — this page decides "there is nothing to show" for both reasons at once.
const isEmpty = computed(
  () =>
    notSetUp.value || (broadcasts.hasLoaded.value && rows.value.length === 0)
);

const composeUrl = computed(() =>
  accountId.value === null
    ? null
    : frontendURL(`accounts/${accountId.value}/campaigns/broadcasts/new`)
);

const compose = () => {
  if (composeUrl.value) router.push(composeUrl.value);
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.BROADCASTS.TITLE')"
    :is-fetching="broadcasts.isFetching.value || channels.isFetching.value"
    :error-message="errorMessage"
    :is-empty="isEmpty"
    :button-label="isEmpty ? '' : t('WASLO.BROADCASTS.NEW')"
    @click="compose"
    @retry="reload"
  >
    <template #body>
      <div class="flex flex-col gap-8 pb-10">
        <!--
          The explanation stays on the page rather than living only in the empty state. An
          owner who has sent four broadcasts still needs the two facts in it on the screen
          where she starts a fifth: WhatsApp charges per delivered message, and there is no
          taking one back.
        -->
        <WasloExplainer
          icon="i-lucide-megaphone"
          :note="t('WASLO.BROADCASTS.SPOTLIGHT')"
        />

        <section class="flex flex-col gap-4" data-waslo-broadcasts>
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.LIST.HEADING')"
            :description="t('WASLO.BROADCASTS.LIST.DESCRIPTION')"
          />
          <ul
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-broadcast-list
          >
            <BroadcastRow
              v-for="broadcast in rows"
              :key="broadcast.id"
              :broadcast="broadcast"
              :account-id="accountId"
            />
          </ul>
        </section>
      </div>
    </template>

    <template #emptyState>
      <!-- P-5.5: "Not set up yet", with the one thing she can do about it. -->
      <NotSetUp
        v-if="notSetUp"
        :account-id="accountId"
        data-waslo-broadcasts-empty
      />
      <!--
        `:show-backdrop="false"` for the same reason as the Answers library: the default
        paints placeholder rows behind a gradient, which four independent critics read as a
        broken page.
      -->
      <EmptyStateLayout
        v-else
        :title="t('WASLO.BROADCASTS.EMPTY.TITLE')"
        :subtitle="t('WASLO.BROADCASTS.EMPTY.BODY')"
        :show-backdrop="false"
        :action-perms="['administrator']"
        data-waslo-broadcasts-empty
      >
        <template #actions>
          <Button
            :label="t('WASLO.BROADCASTS.EMPTY.ACTION')"
            icon="i-lucide-plus"
            data-waslo-broadcasts-empty-new
            @click="compose"
          />
        </template>
      </EmptyStateLayout>
    </template>
  </WasloPageLayout>
</template>
